<template>
  <div class="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
    <div class="w-full max-w-sm space-y-10">
      <div>
        <img class="mx-auto h-30 w-auto dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
        <img class="mx-auto h-30 w-auto not-dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
        <h2
          class="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white"
        >
          Forgot your password?
        </h2>
        <p class="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
          Enter your email and we will send reset instructions if an account exists.
        </p>
      </div>

      <form v-if="!submitted" class="space-y-6" @submit.prevent="onSubmit">
        <div>
          <input
            id="email"
            name="email"
            type="email"
            autocomplete="email"
            required
            autofocus
            aria-label="Email"
            class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
            placeholder="Email"
            v-model.trim="email"
            :disabled="loading"
          />
        </div>

        <div class="space-y-2">
          <button
            type="submit"
            :disabled="loading || !email"
            class="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:shadow-none dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500"
          >
            <span v-if="loading">Sending…</span>
            <span v-else>Send reset link</span>
          </button>
          <p v-if="error" class="text-sm text-red-600 dark:text-red-400" role="alert">
            {{ error }}
          </p>
        </div>
      </form>

      <div
        v-else
        class="rounded-md border border-gray-200 p-4 text-sm text-gray-700 dark:border-white/10 dark:text-gray-300"
        role="status"
      >
        If an account exists for that email, we have sent password reset instructions.
      </div>

      <p class="text-center text-sm/6 text-gray-500 dark:text-gray-400">
        <router-link
          :to="{ name: 'Login' }"
          class="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >Back to sign in</router-link
        >
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref} from 'vue';
import {useAuthStore} from '@/stores/auth';

const logoUrl = new URL('../assets/logo_runner_small.png', import.meta.url).href;
const auth = useAuthStore();

const email = ref('');
const loading = ref(false);
const error = ref<string | null>(null);
const submitted = ref(false);

async function onSubmit() {
  if (!email.value) return;
  loading.value = true;
  error.value = null;
  try {
    await auth.forgotPassword(email.value);
    submitted.value = true;
  } catch (e) {
    // Still show the generic success copy for anti-enumeration if the API failed unexpectedly
    // only surface a message for clear network failures.
    if (!(e as { response?: unknown } | null)?.response) {
      error.value = 'Unable to connect to the server. Please check your network.';
    } else {
      submitted.value = true;
    }
  } finally {
    loading.value = false;
  }
}
</script>
